// The Green House sits at ZIP 29302 (Spartanburg, SC).
const LAT = 34.95, LON = -81.93;
const RAD = Math.PI / 180;

const TZ = "America/New_York";

// Wall-clock minutes, UTC offset and day-of-year at the Green House, whatever timezone the viewer is in.
export function localClock(date = new Date()) {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: TZ, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" }).formatToParts(date).map((x) => [x.type, x.value]));
  const wall = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute);
  const utc = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes());
  return { minutes: +p.hour * 60 + +p.minute, offset: Math.round((wall - utc) / 60000), doy: Math.floor((Date.UTC(+p.year, +p.month - 1, +p.day) - Date.UTC(+p.year, 0, 0)) / 86400000) };
}

// NOAA-style sunrise/sunset; returns minutes after local midnight at the Green House.
export function sunTimes(date = new Date(), lat = LAT, lon = LON) {
  const { doy: n, offset: tz } = localClock(date);
  const g = (2 * Math.PI / 365) * (n - 1);
  const eqt = 229.18 * (0.000075 + 0.001868 * Math.cos(g) - 0.032077 * Math.sin(g) - 0.014615 * Math.cos(2 * g) - 0.040849 * Math.sin(2 * g));
  const decl = 0.006918 - 0.399912 * Math.cos(g) + 0.070257 * Math.sin(g) - 0.006758 * Math.cos(2 * g) + 0.000907 * Math.sin(2 * g) - 0.002697 * Math.cos(3 * g) + 0.00148 * Math.sin(3 * g);
  const ha = Math.acos(Math.cos(90.833 * RAD) / (Math.cos(lat * RAD) * Math.cos(decl)) - Math.tan(lat * RAD) * Math.tan(decl)) / RAD;
  return { rise: 720 - 4 * (lon + ha) - eqt + tz, set: 720 - 4 * (lon - ha) - eqt + tz };
}

const NIGHT = ["#0B1433", "#1F2D5C", "#3B4A80"];
const DAWN = ["#3E4C8E", "#D98FA0", "#FFCF9E"];
const DUSK = ["#2B2F6E", "#C25E7C", "#FFA468"];
const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
export const mix = (a, b, t) => `#${hex(a).map((v, i) => Math.round(v + (hex(b)[i] - v) * t).toString(16).padStart(2, "0")).join("")}`;
export const mixAll = (a, b, t) => a.map((c, i) => mix(c, b[i], t));
export { NIGHT as NIGHT_SKY };

// Sky colours, darkness (0 day → 1 night) and sun arc for a moment, blending smoothly through dawn and dusk.
export function skyAt(now = new Date(), daySky, override = null) {
  const { rise, set } = sunTimes(now);
  const m = override ?? localClock(now).minutes;
  const keys = [[rise - 70, NIGHT, 1], [rise, DAWN, 0.45], [rise + 70, daySky, 0], [set - 70, daySky, 0], [set, DUSK, 0.45], [set + 70, NIGHT, 1]];
  let sky = NIGHT, night = 1;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const [t0, s0, n0] = keys[i], [t1, s1, n1] = keys[i + 1];
    if (m >= t0 && m <= t1) { const f = (m - t0) / (t1 - t0); sky = mixAll(s0, s1, f); night = n0 + (n1 - n0) * f; break; }
  }
  const prog = (m - rise) / (set - rise);
  const phase = m < rise - 70 || m > set + 70 ? "night" : m < rise + 70 ? "dawn" : m > set - 70 ? "dusk" : "day";
  return { sky, night, prog, elev: Math.sin(Math.PI * Math.min(1.05, Math.max(-0.05, prog))), phase, rise, set };
}
